import React, { useContext, useState } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import { Box, Button, Link as ExternalLink, Typography } from "@mui/material";

import { SensorAlertCSVExportURL } from "../../../../constants/sensors";
import { toUUID } from "../../../../helpers/s12-helpers";
import { downloadSensorAlertHistoryForOrg as downloadSensorAlertHistoryForOrgAPI } from "api/endpoints/sensors";
import { callOptionsHeaders } from "api/helpers";
import DatePicker from "components/shared/date-picker";
import { SnackbarContext } from "context/snackbar-context";

const OrganisationSensorAlerts = ({
  regionCode,
  roleId,
}: {
  regionCode: string;
  roleId: string;
}) => {
  const { showError } = useContext(SnackbarContext);
  const [isAlertsDownloading, setIsAlertsDownloading] = useState(false);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  );
  const [toDate, setToDate] = useState<Date>(new Date());

  const downloadAlertEventsForOrg = async (
    roleId: string,
    fromDate: Date,
    toDate: Date,
  ) => {
    setIsAlertsDownloading(true);
    const orgID = toUUID(roleId);

    return downloadSensorAlertHistoryForOrgAPI(
      {
        orgId: orgID,
        fromDate,
        toDate,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((alertHistoryURL) => {
        window.location.assign(alertHistoryURL);
        setIsAlertsDownloading(false);
      })
      .catch((error) => {
        showError(error);
        setIsAlertsDownloading(false);
      });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      gap={2}
      sx={{
        padding: 3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        maxWidth: 480,
      }}
    >
      <Box textAlign="left">
        <Typography variant="subtitle1" fontWeight={600}>
          Download sensor alerts
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Export a CSV of all sensor alerts for this organisation within the
          selected date range.{" "}
          <ExternalLink
            href={SensorAlertCSVExportURL}
            target="_blank"
            rel="noreferrer"
            variant="caption"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Learn more about alert CSV fields
            <LaunchIcon sx={{ fontSize: "1em" }} />
          </ExternalLink>
        </Typography>
      </Box>
      <DatePicker
        label="From"
        size="small"
        fullWidth
        defaultDateSelection={fromDate}
        maxDate={toDate}
        onChange={(date: Date | null) => {
          // The picker can call onChange(null) on a cleared/invalid input;
          // ignore it so fromDate never drifts from its Date type -- it's
          // passed straight into the alert-download API call below.
          if (date) {
            setFromDate(date);
          }
        }}
      />
      <DatePicker
        label="To"
        size="small"
        fullWidth
        defaultDateSelection={toDate}
        onChange={(date: Date | null) => {
          if (date) {
            setToDate(date);
          }
        }}
      />
      <Typography variant="caption" color="text.secondary">
        Dates use your local timezone.
      </Typography>
      <Button
        onClick={() => {
          downloadAlertEventsForOrg(roleId, fromDate, toDate);
        }}
        variant="contained"
        fullWidth
        disabled={isAlertsDownloading}
        sx={{ textTransform: "none" }}
        data-testid="btn-download-sensor-alerts"
      >
        {isAlertsDownloading ? "Downloading..." : "Download sensor alerts"}
      </Button>
    </Box>
  );
};

export default OrganisationSensorAlerts;
